import { useState, useEffect } from 'react';
import {
    Typography,
    Button,
    Stack,
    Modal,
    ModalDialog,
    ModalClose,
    FormControl,
    FormLabel,
    Input,
    Select,
    Option,
    Alert,
    Box,
    CircularProgress,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Chip,
    Divider,
    RadioGroup,
    Radio,
    Sheet
} from '@mui/joy';
import createApi, { getBudgets, createBudget, updateBudget, redistributeBudget, getAllowedCategories, createGoal, deleteGoal, contributeToGoal } from '../api';

function BudgetsTab({ initDataRaw }) {
    const [budgets, setBudgets] = useState([]);
    const [goals, setGoals] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [typeSelectOpen, setTypeSelectOpen] = useState(false);
    const [contributeOpen, setContributeOpen] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [contributeAmount, setContributeAmount] = useState('');
    const [contributing, setContributing] = useState(false);
    const [error, setError] = useState('');
    
    // Edit budget modal state
    const [editBudgetOpen, setEditBudgetOpen] = useState(false);
    const [editingBudget, setEditingBudget] = useState(null);
    const [editLimit, setEditLimit] = useState('');
    const [editing, setEditing] = useState(false);
    
    // Redistribute modal state
    const [redistributeOpen, setRedistributeOpen] = useState(false);
    const [fromCategory, setFromCategory] = useState('');
    const [toCategory, setToCategory] = useState('');
    const [redistributeAmount, setRedistributeAmount] = useState('');
    const [redistributing, setRedistributing] = useState(false);
    
    // Collapsed state for zero-budget categories
    const [noBudgetExpanded, setNoBudgetExpanded] = useState(false);
    
    // Данные о доходах и нераспределенных средствах
    const [totalIncome, setTotalIncome] = useState(0);
    const [totalBudgeted, setTotalBudgeted] = useState(0);
    const [categoryBudgeted, setCategoryBudgeted] = useState(0);
    const [goalsBudgeted, setGoalsBudgeted] = useState(0);
    const [unbudgeted, setUnbudgeted] = useState(0);

    // Селекторы месяца/года
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Form state for budget
    const [createType, setCreateType] = useState('budget'); // 'budget' or 'goal'
    const [category, setCategory] = useState('');
    const [monthlyLimit, setMonthlyLimit] = useState('');
    
    // Form state for goal
    const [goalName, setGoalName] = useState('');
    const [targetAmount, setTargetAmount] = useState('');
    const [targetDate, setTargetDate] = useState('');
    
    const [creating, setCreating] = useState(false);

    const api = createApi(initDataRaw);

    useEffect(() => {
        loadData();
    }, [selectedMonth, selectedYear]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [budgetsRes, categoriesRes] = await Promise.all([
                getBudgets(api, selectedMonth, selectedYear),
                getAllowedCategories(api)
            ]);
            const { budgets, goals, total_income, total_budgeted, category_budgeted, goals_budgeted, unbudgeted } = budgetsRes.data;
            setBudgets(budgets);
            setGoals(goals || []);
            setTotalIncome(total_income);
            setTotalBudgeted(total_budgeted);
            setCategoryBudgeted(category_budgeted || 0);
            setGoalsBudgeted(goals_budgeted || 0);
            setUnbudgeted(unbudgeted);
            setCategories(categoriesRes.data);
        } catch (err) {
            console.error('Error loading budgets:', err);
            setError('Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError('');

        if (createType === 'budget') {
            if (!category) {
                setError('Выберите категорию');
                return;
            }

            if (!monthlyLimit || parseFloat(monthlyLimit) <= 0) {
                setError('Введите корректную сумму');
                return;
            }

            setCreating(true);

            try {
                await createBudget(api, {
                    category,
                    monthly_limit: parseFloat(monthlyLimit),
                    month: selectedMonth,
                    year: selectedYear
                });

                setCategory('');
                setMonthlyLimit('');
                setCreateOpen(false);
                loadData();
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            } catch (err) {
                console.error('Error creating budget:', err);
                setError(err.response?.data?.error || 'Ошибка при создании бюджета');
            } finally {
                setCreating(false);
            }
        } else {
            // Creating goal
            if (!goalName.trim()) {
                setError('Введите название цели');
                return;
            }

            if (!targetAmount || parseFloat(targetAmount) <= 0) {
                setError('Введите корректную сумму');
                return;
            }

            setCreating(true);

            try {
                await createGoal(api, {
                    name: goalName.trim(),
                    target_amount: parseFloat(targetAmount),
                    target_date: targetDate || null
                });

                setGoalName('');
                setTargetAmount('');
                setTargetDate('');
                setCreateOpen(false);
                loadData();
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            } catch (err) {
                console.error('Error creating goal:', err);
                setError(err.response?.data?.error || 'Ошибка при создании цели');
            } finally {
                setCreating(false);
            }
        }
    };

    const handleDeleteGoal = async (id, name) => {
        if (window.confirm(`Удалить цель "${name}"?`)) {
            try {
                await deleteGoal(api, id);
                loadData();
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            } catch (err) {
                console.error('Error deleting goal:', err);
            }
        }
    };

    const openContributeModal = (goal) => {
        setSelectedGoal(goal);
        setContributeAmount('');
        setContributeOpen(true);
    };

    const handleContribute = async () => {
        if (!contributeAmount || parseFloat(contributeAmount) <= 0) return;

        setContributing(true);
        try {
            await contributeToGoal(api, selectedGoal.id, parseFloat(contributeAmount));
            setContributeOpen(false);
            setSelectedGoal(null);
            setContributeAmount('');
            loadData();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } catch (err) {
            console.error('Error contributing:', err);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
        } finally {
            setContributing(false);
        }
    };

    const handleAddClick = () => {
        setTypeSelectOpen(true);
    };

    const handleTypeSelect = (type) => {
        setCreateType(type);
        setTypeSelectOpen(false);
        setCreateOpen(true);
        setError('');
    };

    const openEditModal = (budget) => {
        setEditingBudget(budget);
        setEditLimit(budget.monthly_limit.toString());
        setEditBudgetOpen(true);
        setError('');
    };

    const handleEditBudget = async () => {
        const newLimit = parseFloat(editLimit);
        if (isNaN(newLimit) || newLimit < 0) {
            setError('Введите корректную сумму');
            return;
        }

        setEditing(true);
        try {
            if (editingBudget.id) {
                // Update existing budget
                await updateBudget(api, editingBudget.id, { monthly_limit: newLimit });
            } else {
                // Create new budget for category
                await createBudget(api, {
                    category: editingBudget.category,
                    monthly_limit: newLimit,
                    month: selectedMonth,
                    year: selectedYear
                });
            }
            setEditBudgetOpen(false);
            setEditingBudget(null);
            loadData();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } catch (err) {
            console.error('Error updating budget:', err);
            setError(err.response?.data?.error || 'Ошибка при обновлении бюджета');
        } finally {
            setEditing(false);
        }
    };

    const openRedistributeModal = () => {
        setFromCategory('');
        setToCategory('');
        setRedistributeAmount('');
        setRedistributeOpen(true);
        setError('');
    };

    const handleRedistribute = async () => {
        if (!fromCategory || !toCategory) {
            setError('Выберите категории');
            return;
        }

        const amount = parseFloat(redistributeAmount);
        if (isNaN(amount) || amount <= 0) {
            setError('Введите корректную сумму');
            return;
        }

        setRedistributing(true);
        try {
            await redistributeBudget(api, {
                from_category: fromCategory,
                to_category: toCategory,
                amount,
                month: selectedMonth,
                year: selectedYear
            });
            setRedistributeOpen(false);
            loadData();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } catch (err) {
            console.error('Error redistributing:', err);
            setError(err.response?.data?.error || 'Ошибка при перераспределении');
        } finally {
            setRedistributing(false);
        }
    };

    // Категории с бюджетом > 0 или тратами > 0
    const activeBudgets = budgets.filter(b => b.monthly_limit > 0 || b.spent > 0);
    // Категории без бюджета и без трат
    const inactiveBudgets = budgets.filter(b => b.monthly_limit === 0 && b.spent === 0);
    // Категории с установленным лимитом > 0 (для перераспределения)
    const categoriesWithBudget = budgets.filter(b => b.monthly_limit > 0);
    
    // Доступный остаток для from_category
    const fromBudget = categoriesWithBudget.find(b => b.category === fromCategory);
    const availableToRedistribute = fromBudget ? fromBudget.monthly_limit : 0;

    const formatMoney = (value) => {
        return new Intl.NumberFormat('ru-RU').format(value);
    };

    const getProgressColor = (status) => {
        switch (status) {
            case 'exceeded': return 'danger';
            case 'warning': return 'warning';
            default: return 'primary';
        }
    };

    // Категории без установленного бюджета
    const usedCategories = budgets.map(b => b.category);
    const availableCategories = categories.filter(c => !usedCategories.includes(c));

    // Общая статистика трат
    const totalSpent = budgets.reduce((sum, b) => sum + b.spent, 0);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography level="h3">Бюджет</Typography>
                <Stack direction="row" spacing={1}>
                    {categoriesWithBudget.length >= 2 && (
                        <Button size="sm" variant="soft" color="neutral" onClick={openRedistributeModal}>
                            ↔ Перенести
                        </Button>
                    )}
                    <Button size="sm" onClick={handleAddClick}>
                        + Добавить
                    </Button>
                </Stack>
            </Stack>

            {/* Селектор месяца/года */}
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <Select
                    value={selectedMonth}
                    onChange={(e, value) => setSelectedMonth(value)}
                    size="sm"
                    sx={{ minWidth: 120 }}
                >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <Option key={month} value={month}>
                            {new Date(2000, month - 1, 1).toLocaleString('ru-RU', { month: 'long' })}
                        </Option>
                    ))}
                </Select>
                <Select
                    value={selectedYear}
                    onChange={(e, value) => setSelectedYear(value)}
                    size="sm"
                    sx={{ minWidth: 100 }}
                >
                    {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i).map((year) => (
                        <Option key={year} value={year}>{year}</Option>
                    ))}
                </Select>
            </Stack>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Карточка нераспределенных средств */}
            {totalIncome > 0 && (
                <Alert 
                    color={unbudgeted > 0 ? 'warning' : unbudgeted < 0 ? 'danger' : 'success'} 
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography level="body-sm">
                            Доход: {formatMoney(totalIncome)} ₽ / Распределено: {formatMoney(totalBudgeted)} ₽
                        </Typography>
                        {goalsBudgeted > 0 && (
                            <Typography level="body-xs" color="neutral">
                                (категории: {formatMoney(categoryBudgeted)} ₽ + цели: {formatMoney(goalsBudgeted)} ₽)
                            </Typography>
                        )}
                        {unbudgeted > 0 && (
                            <Typography level="title-sm">
                                Нераспределено: {formatMoney(unbudgeted)} ₽
                            </Typography>
                        )}
                        {unbudgeted < 0 && (
                            <Typography level="title-sm">
                                Бюджет превышает доход на {formatMoney(Math.abs(unbudgeted))} ₽
                            </Typography>
                        )}
                        {unbudgeted === 0 && totalBudgeted > 0 && (
                            <Typography level="title-sm">
                                Весь доход распределен
                            </Typography>
                        )}
                    </Box>
                </Alert>
            )}

            {/* Общая статистика */}
            {budgets.length > 0 && (
                <Card variant="soft" sx={{ mb: 3 }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between">
                            <Box>
                                <Typography level="body-sm" color="neutral">Общий лимит</Typography>
                                <Typography level="title-lg">{formatMoney(totalBudgeted)} ₽</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography level="body-sm" color="neutral">Потрачено</Typography>
                                <Typography level="title-lg" color={totalSpent > totalBudgeted ? 'danger' : 'primary'}>
                                    {formatMoney(totalSpent)} ₽
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {activeBudgets.length === 0 && goals.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography level="body-lg" color="neutral">
                        Нет бюджетов и целей
                    </Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                        Добавьте бюджеты для контроля трат или цели для накоплений
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2}>
                    {/* Список активных бюджетов */}
                    {activeBudgets.map(budget => (
                        <Card key={`budget-${budget.category}`} variant="outlined">
                            <CardContent sx={{ pb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography level="title-md">
                                                {budget.category.toLowerCase()}
                                            </Typography>
                                            {budget.status === 'exceeded' && (
                                                <Chip size="sm" color="danger" variant="soft">
                                                    Превышен
                                                </Chip>
                                            )}
                                            {budget.status === 'warning' && (
                                                <Chip size="sm" color="warning" variant="soft">
                                                    Почти исчерпан
                                                </Chip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>

                                <Box sx={{ mt: 1.5 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                        <Typography level="body-sm">
                                            {formatMoney(budget.spent)} ₽
                                        </Typography>
                                        <Typography 
                                            level="body-sm" 
                                            sx={{ 
                                                cursor: 'pointer', 
                                                textDecoration: 'underline',
                                                textDecorationStyle: 'dashed',
                                                '&:hover': { color: 'primary.main' }
                                            }}
                                            onClick={() => openEditModal(budget)}
                                        >
                                            {formatMoney(budget.monthly_limit)} ₽
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        determinate
                                        value={Math.min(budget.progress, 100)}
                                        color={getProgressColor(budget.status)}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                        Осталось: {formatMoney(Math.max(0, budget.remaining))} ₽
                                        {budget.remaining < 0 && ` (превышено на ${formatMoney(Math.abs(budget.remaining))} ₽)`}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}

                    {/* Список целей */}
                    {goals.map(goal => (
                        <Card key={`goal-${goal.id}`} variant="outlined">
                            <CardContent sx={{ pb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography level="title-md">
                                                🎯 {goal.name}
                                            </Typography>
                                            {goal.monthlyFunded && (
                                                <Chip size="sm" color="success" variant="soft">
                                                    ✓ внесено
                                                </Chip>
                                            )}
                                        </Stack>
                                        {goal.target_date && (
                                            <Typography level="body-xs" color="neutral">
                                                До {new Date(goal.target_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                {goal.monthsLeft && ` (${goal.monthsLeft} мес.)`}
                                            </Typography>
                                        )}
                                    </Box>
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="danger"
                                        onClick={() => handleDeleteGoal(goal.id, goal.name)}
                                    >
                                        ✕
                                    </IconButton>
                                </Stack>

                                <Box sx={{ mt: 1.5 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                        <Typography level="body-sm">
                                            {formatMoney(goal.current_amount)} ₽
                                        </Typography>
                                        <Typography level="body-sm">
                                            {formatMoney(goal.target_amount)} ₽
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        determinate
                                        value={Math.min(goal.progress, 100)}
                                        color={goal.progress >= 100 ? 'success' : 'primary'}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                    {goal.monthlyAmount && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography level="body-xs" color={goal.monthlyFunded ? 'success' : 'neutral'}>
                                                {goal.monthlyFunded 
                                                    ? `✓ Внесено за месяц: ${formatMoney(goal.monthlyContributed)} ₽`
                                                    : `${formatMoney(goal.monthlyAmount)} ₽/мес${goal.monthlyContributed > 0 ? ` (внесено ${formatMoney(goal.monthlyContributed)} ₽)` : ''}`
                                                }
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Button
                                    variant="soft"
                                    color="primary"
                                    size="sm"
                                    fullWidth
                                    sx={{ mt: 1.5 }}
                                    onClick={() => openContributeModal(goal)}
                                >
                                    Внести
                                </Button>
                            </CardContent>
                        </Card>
                    ))}

                    {/* Свернутый блок категорий без бюджета */}
                    {inactiveBudgets.length > 0 && (
                        <Card variant="soft" sx={{ cursor: 'pointer' }} onClick={() => setNoBudgetExpanded(!noBudgetExpanded)}>
                            <CardContent sx={{ py: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography level="body-sm">
                                        {noBudgetExpanded ? '▾' : '▸'} Без бюджета ({inactiveBudgets.length})
                                    </Typography>
                                </Stack>
                                {!noBudgetExpanded && (
                                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                        {inactiveBudgets.slice(0, 5).map(b => b.category.toLowerCase()).join(', ')}
                                        {inactiveBudgets.length > 5 && '...'}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Развернутый список категорий без бюджета */}
                    {noBudgetExpanded && inactiveBudgets.map(budget => (
                        <Card key={`budget-inactive-${budget.category}`} variant="plain" sx={{ bgcolor: 'background.level1' }}>
                            <CardContent sx={{ py: 1.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography level="body-sm" color="neutral">
                                        {budget.category.toLowerCase()}
                                    </Typography>
                                    <Button
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditModal(budget);
                                        }}
                                    >
                                        Задать лимит
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}

            {/* Modal выбора типа */}
            <Modal open={typeSelectOpen} onClose={() => setTypeSelectOpen(false)}>
                <ModalDialog sx={{ width: '90%', maxWidth: 300 }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Что добавить?
                    </Typography>
                    <Stack spacing={1.5}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={() => handleTypeSelect('budget')}
                            sx={{ justifyContent: 'flex-start', py: 1.5 }}
                        >
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography level="title-sm">Бюджет</Typography>
                                <Typography level="body-xs" color="neutral">
                                    Лимит на категорию на месяц
                                </Typography>
                            </Box>
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={() => handleTypeSelect('goal')}
                            sx={{ justifyContent: 'flex-start', py: 1.5 }}
                        >
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography level="title-sm">🎯 Цель</Typography>
                                <Typography level="body-xs" color="neutral">
                                    Накопить сумму к дате
                                </Typography>
                            </Box>
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>

            {/* Modal создания бюджета/цели */}
            <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
                <ModalDialog sx={{ width: '90%', maxWidth: 400 }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        {createType === 'budget' ? 'Новый бюджет' : 'Новая цель'}
                    </Typography>

                    {error && (
                        <Alert color="danger" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleCreate}>
                        <Stack spacing={2}>
                            {createType === 'budget' ? (
                                <>
                                    <FormControl required>
                                        <FormLabel>Категория</FormLabel>
                                        <Select
                                            placeholder="Выберите категорию"
                                            value={category}
                                            onChange={(e, newValue) => setCategory(newValue)}
                                            slotProps={{
                                                listbox: { sx: { maxHeight: 250 } }
                                            }}
                                        >
                                            {availableCategories.map((cat) => (
                                                <Option key={cat} value={cat}>
                                                    {cat.toLowerCase()}
                                                </Option>
                                            ))}
                                        </Select>
                                        {availableCategories.length === 0 && categories.length > 0 && (
                                            <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                                                Для всех категорий уже установлены бюджеты
                                            </Typography>
                                        )}
                                    </FormControl>

                                    <FormControl required>
                                        <FormLabel>Лимит на месяц</FormLabel>
                                        <Input
                                            type="number"
                                            placeholder="30000"
                                            value={monthlyLimit}
                                            onChange={(e) => setMonthlyLimit(e.target.value)}
                                            slotProps={{
                                                input: { min: 0, step: 100 }
                                            }}
                                            endDecorator="₽"
                                        />
                                    </FormControl>
                                </>
                            ) : (
                                <>
                                    <FormControl required>
                                        <FormLabel>Название</FormLabel>
                                        <Input
                                            placeholder="Например: Отпуск в Италии"
                                            value={goalName}
                                            onChange={(e) => setGoalName(e.target.value)}
                                            autoFocus
                                        />
                                    </FormControl>

                                    <FormControl required>
                                        <FormLabel>Целевая сумма</FormLabel>
                                        <Input
                                            type="number"
                                            placeholder="100000"
                                            value={targetAmount}
                                            onChange={(e) => setTargetAmount(e.target.value)}
                                            slotProps={{
                                                input: { min: 0, step: 1 }
                                            }}
                                            endDecorator="₽"
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Дата (необязательно)</FormLabel>
                                        <Input
                                            type="date"
                                            value={targetDate}
                                            onChange={(e) => setTargetDate(e.target.value)}
                                            slotProps={{
                                                input: {
                                                    min: new Date().toISOString().split('T')[0]
                                                }
                                            }}
                                        />
                                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                            Если указать дату, будет расчёт ежемесячного взноса
                                        </Typography>
                                    </FormControl>
                                </>
                            )}

                            <Button
                                type="submit"
                                loading={creating}
                                fullWidth
                                sx={{ mt: 2 }}
                                disabled={createType === 'budget' && availableCategories.length === 0 && categories.length > 0}
                            >
                                Создать
                            </Button>
                        </Stack>
                    </form>
                </ModalDialog>
            </Modal>

            {/* Modal для внесения суммы в цель */}
            <Modal open={contributeOpen} onClose={() => setContributeOpen(false)}>
                <ModalDialog sx={{ width: '90%', maxWidth: 300 }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Внести в "{selectedGoal?.name}"
                    </Typography>

                    <FormControl>
                        <FormLabel>Сумма</FormLabel>
                        <Input
                            type="number"
                            placeholder="0"
                            value={contributeAmount}
                            onChange={(e) => setContributeAmount(e.target.value)}
                            autoFocus
                            slotProps={{
                                input: { min: 0, step: 1 }
                            }}
                            endDecorator="₽"
                        />
                    </FormControl>

                    {selectedGoal && (
                        <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                            Осталось: {formatMoney(selectedGoal.remaining)} ₽
                        </Typography>
                    )}

                    <Button
                        onClick={handleContribute}
                        loading={contributing}
                        fullWidth
                        sx={{ mt: 2 }}
                        disabled={!contributeAmount || parseFloat(contributeAmount) <= 0}
                    >
                        Внести
                    </Button>
                </ModalDialog>
            </Modal>

            {/* Modal редактирования лимита */}
            <Modal open={editBudgetOpen} onClose={() => setEditBudgetOpen(false)}>
                <ModalDialog sx={{ width: '90%', maxWidth: 300 }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        {editingBudget?.category?.toLowerCase()}
                    </Typography>

                    {error && (
                        <Alert color="danger" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <FormControl>
                        <FormLabel>Лимит на месяц</FormLabel>
                        <Input
                            type="number"
                            placeholder="0"
                            value={editLimit}
                            onChange={(e) => setEditLimit(e.target.value)}
                            autoFocus
                            slotProps={{
                                input: { min: 0, step: 100 }
                            }}
                            endDecorator="₽"
                        />
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                            Установите 0 чтобы снять лимит
                        </Typography>
                    </FormControl>

                    <Button
                        onClick={handleEditBudget}
                        loading={editing}
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        Сохранить
                    </Button>
                </ModalDialog>
            </Modal>

            {/* Modal перераспределения средств */}
            <Modal open={redistributeOpen} onClose={() => setRedistributeOpen(false)}>
                <ModalDialog sx={{ width: '90%', maxWidth: 350 }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Перераспределить средства
                    </Typography>

                    {error && (
                        <Alert color="danger" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Stack spacing={2}>
                        <FormControl>
                            <FormLabel>Из категории</FormLabel>
                            <Select
                                placeholder="Выберите категорию"
                                value={fromCategory}
                                onChange={(e, value) => setFromCategory(value)}
                                slotProps={{
                                    listbox: { sx: { maxHeight: 200 } }
                                }}
                            >
                                {categoriesWithBudget
                                    .filter(b => b.category !== toCategory)
                                    .map((b) => (
                                        <Option key={b.category} value={b.category}>
                                            {b.category.toLowerCase()} ({formatMoney(b.monthly_limit)} ₽)
                                        </Option>
                                    ))}
                            </Select>
                            {fromCategory && (
                                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                    Доступно: {formatMoney(availableToRedistribute)} ₽
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl>
                            <FormLabel>В категорию</FormLabel>
                            <Select
                                placeholder="Выберите категорию"
                                value={toCategory}
                                onChange={(e, value) => setToCategory(value)}
                                slotProps={{
                                    listbox: { sx: { maxHeight: 200 } }
                                }}
                            >
                                {budgets
                                    .filter(b => b.category !== fromCategory)
                                    .map((b) => (
                                        <Option key={b.category} value={b.category}>
                                            {b.category.toLowerCase()} ({formatMoney(b.monthly_limit)} ₽)
                                        </Option>
                                    ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Сумма</FormLabel>
                            <Input
                                type="number"
                                placeholder="0"
                                value={redistributeAmount}
                                onChange={(e) => setRedistributeAmount(e.target.value)}
                                slotProps={{
                                    input: { min: 0, max: availableToRedistribute, step: 100 }
                                }}
                                endDecorator="₽"
                            />
                        </FormControl>

                        <Button
                            onClick={handleRedistribute}
                            loading={redistributing}
                            fullWidth
                            disabled={!fromCategory || !toCategory || !redistributeAmount || parseFloat(redistributeAmount) <= 0}
                        >
                            Перенести
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </Box>
    );
}

export default BudgetsTab;
